import type { CascaderOption } from '@/components';
import { getOrganizationNamesWithUpper } from '@/features/analysis/_components/analysis-visualizer/AnalysisVisualizer';
import { useNestedExpenditureBudgetPurposeOptions } from '@/features/master-table/hooks';
import { usePoliciesOptions } from '@/features/master-table/policy/_hooks/policy';
import { PsSkeletonContents } from '@/features/search/_components/projects/_common/PsSkeletonContents';
import type { AnalysisSearchQueryStates } from '@/features/search/_hooks/analysis-search';
import type { ProjectsSearchQueryStates } from '@/features/search/_hooks/projects-search';
import { useQueryStore } from '@/features/search/_hooks/query-store';
import type { QueryStoreQueryModel } from '@/features/search/_models/query-store';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/hooks';
import { type NestedOptionModel, getSelectedCascaderIds, isPS } from '@/utils';
import {
  type ContextType,
  type Dispatch,
  type FC,
  type MutableRefObject,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { z } from 'zod';
import type {
  InputCascaderAndAccountingLabels,
  ProjectsSearchAccordionProviderType,
} from '../../../_utils/projects-search';
import { AnalysisSearchSkeletonContents } from './AnalysisSearchSkeletonContents';

type AccordionState = {
  basicOpen: boolean;
  // 以下は事業検索のみ
  additionalOpen: boolean;
  analysisOpen: boolean;
};

type SearchQueryStates = {
  cascaderTargetIds: Pick<
    z.infer<typeof QueryStoreQueryModel>,
    | 'expenditureBudgetPurposeIds'
    | 'organizationIds'
    | 'otherResponsibleOrganizationIds'
    | 'policyIds'
  >;
  data: {
    organizationsOptions: CascaderOption[] | undefined;
    policiesOptions: CascaderOption[] | undefined;
    expenditureBudgetPurposesOptions:
      | z.infer<typeof NestedOptionModel>
      | undefined;
  };
  inputCascaderAndAccountingLabels: MutableRefObject<InputCascaderAndAccountingLabels>;
  type: 'project' | 'group' | 'analysis';
  accordionState: AccordionState;
  setAccordionState: Dispatch<SetStateAction<AccordionState>>;
};

const Context = createContext<SearchQueryStates | undefined>(undefined);

export const useProjectsSearchFormProviderState = (): NonNullable<
  ContextType<typeof Context>
> => {
  const context = useContext(Context);
  if (!context) {
    throw new Error(
      'ProjectsSearchAccordionProviderの配下で呼び出してください。',
    );
  }
  return context;
};

export const ProjectsSearchAccordionProvider: FC<
  PropsWithChildren<{
    type: ProjectsSearchAccordionProviderType;
    searchQueryStates: ProjectsSearchQueryStates | AnalysisSearchQueryStates;
  }>
> = ({ children, type, searchQueryStates }) => {
  const [accordionState, setAccordionState] = useState<AccordionState>({
    basicOpen: false,
    additionalOpen: false,
    analysisOpen: false,
  });

  const inputCascaderAndAccountingLabels =
    useRef<InputCascaderAndAccountingLabels>({
      nestedOrganizationIds: [],
      nestedOtherResponsibleOrganizationIds: [],
      nestedPolicyIds: [],
      nestedExpenditureBudgetPurposeIds: [],
      accountingTypeIds: [],
    });
  const organizationsOptions = useOrganizationOptionsRecursive();

  // const enabledGetOrganizationIdsForPs: boolean = !!(
  //   isPS() && searchQueryStates.organizationIds?.length
  // );

  const selectedOrganizationIdsForPs = searchQueryStates.organizationIds?.map(
    (v) => {
      return getOrganizationNamesWithUpper({
        id: v,
        organizations: organizationsOptions.data ?? [],
      }).ids.map((v) => v.toString());
    },
  );

  // const selectedOrganizationIdsForPs = useRecursiveOrganizationFilteredByKey({
  //   enabled: enabledGetOrganizationIdsForPs,
  //   params: {
  //     ids: searchQueryStates.organizationIds ?? [],
  //   },
  //   options: {
  //     filterBy: 'id',
  //     separator: undefined,
  //     reverse: true,
  //   },
  // });
  const policiesOptions = usePoliciesOptions();
  const expenditureBudgetPurposesOptions =
    useNestedExpenditureBudgetPurposeOptions({
      params: { type: 'revenueNature' },
    });

  const selectedOtherResponsibleOrganizationIds = useMemo(() => {
    const otherResponsibleOrganizationIdsParamForPs =
      searchQueryStates.otherResponsibleOrganizationIds;
    if (
      otherResponsibleOrganizationIdsParamForPs &&
      organizationsOptions.data
    ) {
      return getSelectedCascaderIds(
        organizationsOptions.data,
        otherResponsibleOrganizationIdsParamForPs,
      );
    }
    return [];
  }, [
    organizationsOptions.data,
    searchQueryStates.otherResponsibleOrganizationIds,
  ]);

  const selectedPolicyIdsForPs = useMemo(() => {
    const policyIdsParamForPs = searchQueryStates.policyIds;
    if (policyIdsParamForPs && policiesOptions.data) {
      return getSelectedCascaderIds(policiesOptions.data, policyIdsParamForPs);
    }
    return [];
  }, [policiesOptions.data, searchQueryStates.policyIds]);

  const selectedExpenditureBudgetPurposeIdsForPs = useMemo(() => {
    const expenditureBudgetPurposeIdsParamForPs =
      searchQueryStates.expenditureBudgetPurposeIds;
    if (
      expenditureBudgetPurposeIdsParamForPs &&
      expenditureBudgetPurposesOptions.data
    ) {
      return getSelectedCascaderIds(
        expenditureBudgetPurposesOptions.data,
        expenditureBudgetPurposeIdsParamForPs,
      );
    }
    return [];
  }, [
    expenditureBudgetPurposesOptions.data,
    searchQueryStates.expenditureBudgetPurposeIds,
  ]);

  const queryStore = useQueryStore(searchQueryStates.cascader);
  const query = queryStore.data?.query;

  const isLoading =
    policiesOptions.isLoading ||
    organizationsOptions.isLoading ||
    expenditureBudgetPurposesOptions.isLoading ||
    // (enabledGetOrganizationIdsForPs &&
    //   selectedOrganizationIdsForPs.isLoading) ||
    (!isPS() && queryStore.isLoading && searchQueryStates.cascader);

  if (isLoading) {
    return isPS() ? (
      type === 'analysis' ? (
        <AnalysisSearchSkeletonContents />
      ) : (
        <PsSkeletonContents />
      )
    ) : null;
  }

  return (
    <Context.Provider
      value={{
        type,
        inputCascaderAndAccountingLabels: inputCascaderAndAccountingLabels,
        data: {
          organizationsOptions: organizationsOptions.data,
          policiesOptions: policiesOptions.data,
          expenditureBudgetPurposesOptions:
            expenditureBudgetPurposesOptions.data,
        },
        accordionState,
        setAccordionState,
        cascaderTargetIds: isPS()
          ? {
              expenditureBudgetPurposeIds:
                selectedExpenditureBudgetPurposeIdsForPs,
              organizationIds: selectedOrganizationIdsForPs ?? null,
              otherResponsibleOrganizationIds:
                selectedOtherResponsibleOrganizationIds,
              policyIds: selectedPolicyIdsForPs,
            }
          : {
              expenditureBudgetPurposeIds:
                query?.expenditureBudgetPurposeIds ?? null,
              organizationIds: query?.organizationIds ?? null,
              otherResponsibleOrganizationIds:
                query?.otherResponsibleOrganizationIds ?? null,
              policyIds: query?.policyIds ?? null,
            },
      }}
    >
      {children}
    </Context.Provider>
  );
};
